import MockAdapter from 'axios-mock-adapter';
import axios from 'axios';
import {
  LoginResponse,
  type ApiLoginResponse,
  type AxleAuthRequest,
  type AxleOutgoingTokenRequest,
  type SwapAxleTwrnRequest,
  type SwapAxleTwrnResponse,
} from '@/types/auth';
import {
  type ApiTeamBudgetList,
  TeamBudgetOverview,
  type CreateBudgetRequestModel,
  type ApiTeamBudgetPositionTarget,
  type UpdateTeamBudgetLineItemRequestModel,
  type UpdateOrderingRequestModel,
  type BudgetStatusEnum,
  type ApiOrgBudgetOverview,
  OrgBudgetOverviewSummary,
  OrgBudget,
  type ApiOrgBudgetList,
} from '@/types/budgets';
import { type CreateAthleteRequestModel, type ApiEligibleAthlete, EligibleAthlete, type UpdateAthleteScholarshipsRequestModel, type ApiTeamAthleteListingRequestBody, type ApiTeamAthlete, TeamAthlete, type RemoveAthleteRequestModel, type RestoreDeletedAthleteRequestModel, type ApiTeamAthleteProfile, TeamAthleteProfile, type IndexTeamAthletesResponse, type IndexTeamAthletesParams } from '@/types/athletes';
import { EVENTS, trackEvent } from '@/plugins/gainsight';
import {
  AxleProfile,
  LinkableProfilesResponse,
  type ApiAxleProfile,
  type ApiAxleToken,
  type ApiLinkableProfilesResponse,
} from '@/types/teamworks';
import { Me, type ApiMe } from '@/types/users';
import type {
  UpdateTagRequestModel,
  AthleteTagRequestModel,
} from '@/types/tags';
import {
  Agreement,
  AgreementAthlete,
  AgreementPreview,
  AgreementStats,
  AthleteContract,
  ContractTemplate,
  DraftAgreement,
  TeamAgreementAthlete,
  type GenerateAgreementPreviewRequestModel,
  type ApiAgreement,
  type ApiAgreementAthlete,
  type ApiAgreementByContractRequestBody,
  type ApiAgreementPreview,
  type ApiAgreementStats,
  type ApiAthleteContract,
  type ApiContractTemplate,
  type ApiDraftAgreement,
  type ApiTeamAgreementAthlete,
  type ContractTemplateResponse,
  type CreateOrUpdateAgreementRequestModel,
} from '@/types/agreements';
import { PaginationMetadata, type ApiPaginationMetadata } from '@/types/api';
import { PaymentRequest, PaymentRequestWithAgreement, type ApiPaymentRequest, type ApiPaymentRequestListingRequestBody, type ApiPaymentRequestWithAgreement, type PaymentRequestType, type UpdatePaymentRequestRequestModel } from '@/types/paymentRequests';
import mockPaymentRequests from '@/testUtils/mockData/paymentRequests.json';
import { calculateTotalValue } from '@/utils/paymentUtils';
import { AwardCategory, ScholarshipPackageListing, type ApiAwardCategory, type ApiScholarshipPackageListing, type CreateScholarshipPackageRequestModel, type AwardCategoryPayload } from '@/types/scholarships';
import { AcademicYear, type ApiAcademicYear } from '@/types/academicYears';
import { boolToInt } from '@/utils/helpers';
import { Permission, UserPermissionListing, type ApiPermission, type ApiUserPermissionsListingResponse, type ApiUserPermissionsResponse } from '@/types/permissions';
import { TeamPosition, type ApiTeamPosition } from '@/types/positions';
import { type ApiTeamsListingResponse, TeamsListingResponse } from '@/types/teams';

export function useGmApi() {
  const mock = new MockAdapter(axios, { delayResponse: 250 });

  mock.onGet(/\/teams\/.*\/agreements\/.*\/payments\/list/).reply(200, {
    data: mockPaymentRequests,
    metadata: {
      per_page: '25',
      next_cursor: null,
      prev_cursor: null,
    },
  });

  mock.onGet(/\/teams\/.*\/agreements\/.*\/payments\/list/).reply(200, {
    data: mockPaymentRequests,
    metadata: {
      per_page: '25',
      next_cursor: null,
      prev_cursor: null,
    },
  });


  mock.onAny().passThrough();

  // get auth/user
  const loginAxle = async (data: AxleAuthRequest) => {
    const res = await axios.post<ApiLoginResponse>(
      'auth/axle/token/from',
      data,
    );
    return new LoginResponse(res.data);
  };

  const swapAxleTwrnForGmTwrn = async (axleTwrn: string): Promise<SwapAxleTwrnResponse> => {
    const payload: SwapAxleTwrnRequest = {
      axle_twrn: axleTwrn,
    };
    const res = await axios.post<SwapAxleTwrnResponse>('auth/axle/twrn/from', payload);
    return res.data;
  };

  /**
   * Requests `auth/app-switcher` to get axle profile data.
   *
   * The endpoint name is misleading as it returns more than just app switcher details.
   * For this reason, we've named the service method more appropriately.
   * @returns Axle profile data
   */
  const getAxleProfile = async () => {
    const { data } = await axios.get<ApiAxleProfile>('auth/app-switcher');
    return new AxleProfile(data);
  };

  // get user's data
  const getMe = async () => {
    const res = await axios.get<ApiMe>('users/me');
    return new Me(res.data);
  };

  // get outgoing axle token
  const getOutgoingAxleToken = async (data?: AxleOutgoingTokenRequest) => {
    const res = await axios.post<ApiAxleToken>(
      'auth/axle/token/to',
      data,
    );
    return res.data;
  };

  // logout
  const logout = async () => {
    const res = await axios.delete('auth/logout');
    return res;
  };

  // get budgets
  const getTeamBudgets = async (teamTwrn: string) => {
    const res = await axios.get<ApiTeamBudgetList>(`teams/${teamTwrn}/budgets`);
    return res.data.data.map((budget) => new TeamBudgetOverview(budget, teamTwrn));
  };


  // edit budget
  const editBudget = async (
    teamTwrn: string,
    budgetTwrn: string,
    budget: CreateBudgetRequestModel,
  ) => {
    const res = await axios.put<CreateBudgetRequestModel>(
      `teams/${teamTwrn}/budgets/${budgetTwrn}`,
      budget,
    );
    return res;
  };

  // change budget to active
  const changeBudgetStatus = async (teamTwrn: string, budgetTwrn: string, status: BudgetStatusEnum) => {
    const res = await axios.post(`teams/${teamTwrn}/budgets/${budgetTwrn}/status`, {
      status,
    });
    return res;
  };

  // save budget athletes line items
  const saveLineItems = async (
    teamTwrn: string,
    budgetTwrn: string,
    lineItems: UpdateTeamBudgetLineItemRequestModel,
  ) => {
    const res = await axios.patch(
      `teams/${teamTwrn}/budgets/${budgetTwrn}/line-items`,
      lineItems,
    );
    if (res.status === 200) {
      trackEvent(EVENTS.BUDGET_SAVED, {
        teamTwrn,
        budgetTwrn,
      });
    }
    return res;
  };

  // save position Targets
  const savePositionTargets = async (
    teamTwrn: string,
    budgetTwrn: string,
    positionTargets: ApiTeamBudgetPositionTarget[],
  ) => {
    const res = await axios.put(
      `teams/${teamTwrn}/budgets/${budgetTwrn}/position-targets`,
      {
        position_targets: positionTargets, // Wrap in an object
      },
    );
    return res;
  };

  // delete budget
  const deleteBudget = async (teamTwrn: string, budgetTwrn: string) => {
    await axios.delete(`teams/${teamTwrn}/budgets/${budgetTwrn}`);
  };

  // create player
  const createPlayer = async (
    teamTwrn: string,
    budgetTwrn: string,
    athlete: CreateAthleteRequestModel,
  ) => {
    const res = await axios.post(
      `teams/${teamTwrn}/budgets/${budgetTwrn}/athletes`,
      athlete,
    );
    if (res.status === 201) {
      trackEvent(EVENTS.ATHLETE_CREATED, {
        teamTwrn,
      });
    }
    return res;
  };

  // delete player
  const deleteAthlete = async (
    teamTwrn: string,
    budgetTwrn: string,
    athleteTwrn: string,
  ) => {
    await axios.delete(
      `teams/${teamTwrn}/budgets/${budgetTwrn}/athletes/${athleteTwrn}`,
    );
  };

  // get tags
  const getTags = async (orgTwrn: string, teamTwrn: string) => {
    const res = await axios.get(
      `orgs/${orgTwrn}/athlete-tags/?team=${teamTwrn}`,
    );
    return res.data.data;
  };

  // add tag
  const createTag = async (orgTwrn: string, tag: UpdateTagRequestModel) => {
    const res = await axios.post(`orgs/${orgTwrn}/athlete-tags`, tag);
    return res.data;
  };

  // update tag
  const updateTag = async (
    orgTwrn: string,
    tagUuid: string,
    tag: UpdateTagRequestModel,
  ) => {
    const res = await axios.put(`orgs/${orgTwrn}/athlete-tags/${tagUuid}`, tag);
    return res.data.data;
  };

  // apply tag to athlete
  const applyTag = async (
    orgTwrn: string,
    athleteTwrn: string,
    tags: AthleteTagRequestModel,
  ) => {
    const res = await axios.patch(
      `orgs/${orgTwrn}/athletes/${athleteTwrn}/tags`,
      tags,
    );
    return res.data.data;
  };

  // remove tag from athlete
  const removeTag = async (
    orgTwrn: string,
    athleteTwrn: string,
    tags: AthleteTagRequestModel,
  ) => {
    const res = await axios.delete(
      `orgs/${orgTwrn}/athletes/${athleteTwrn}/tags`,
      { data: tags },
    );
    return res.data.data;
  };

  // update athlete ordering
  const updateAthleteOrder = async (
    teamTwrn: string,
    budgetTwrn: string,
    position: string,
    payload: UpdateOrderingRequestModel[],
  ) => {
    await axios.put(
      `teams/${teamTwrn}/budgets/${budgetTwrn}/positions/${position}/ordering`,
      { data: payload },
    );
  };

  // get agreements
  const getAgreements = async (teamTwrn: string) => {
    const res = await axios.get<{ data: ApiAgreement[] }>(`/teams/${teamTwrn}/agreements`);
    return res.data.data.map((agreement) => new Agreement(agreement));
  };

  const getAgreementsByContract = async (
    teamTwrn: string,
    requestBody: ApiAgreementByContractRequestBody,
  ) => {
    const res = await axios.post<{
      data: ApiTeamAgreementAthlete[];
      metadata: ApiPaginationMetadata;
    }>(`/teams/${teamTwrn}/agreements/athlete-agreements/list`, requestBody);
    return {
      data: res.data.data.map((teamAgreementAthlete) => new TeamAgreementAthlete(teamAgreementAthlete)),
      metadata: res.data.metadata,
    };
  };

  const getDraftAgreement = async (teamTwrn: string, agreementTwrn: string) => {
    const res = await axios.get<ApiDraftAgreement>(`teams/${teamTwrn}/agreements/${agreementTwrn}/draft`);
    return new DraftAgreement(res.data);
  };

  const getContractTemplate = async (teamTwrn: string, contractTemplateTwrn: string) => {
    const res = await axios.get(`teams/${teamTwrn}/contract-templates/${contractTemplateTwrn}`);
    return new ContractTemplate(res.data);
  };

  // Get athletes
  const getEligibleAthletes = async (
    teamTwrn: string,
    options: {
      teamBudgetTwrn?: string;
      teamBudgetGroupTwrn?: string;
      hasAgreements?: boolean;
      onlyTrashed?: boolean;
      withTrashed?: boolean;
      trackEvents?: boolean;
      academicYearDateId?: string;
    } = {},
  ) => {
    const {
      teamBudgetTwrn,
      teamBudgetGroupTwrn,
      hasAgreements,
      onlyTrashed = false,
      withTrashed = true,
      trackEvents = false,
      academicYearDateId,
    } = options;

    const params: IndexTeamAthletesParams = {};
    if (teamBudgetTwrn) params.team_budget = teamBudgetTwrn;
    if (teamBudgetGroupTwrn) params.team_budget_group = teamBudgetGroupTwrn;
    if (hasAgreements !== undefined) params.has_agreements = boolToInt(hasAgreements);
    if (onlyTrashed) params.only_trashed = boolToInt(onlyTrashed);
    if (academicYearDateId) params.academic_year_date_id = academicYearDateId;
    params.with_trashed = boolToInt(withTrashed);

    const res = await axios.get<{ data: ApiEligibleAthlete[] | IndexTeamAthletesResponse[] }>
    (`teams/${teamTwrn}/athletes`, { params });

    if (res.status === 200 && trackEvents) {
      trackEvent(EVENTS.PREFILLED_AGREEMENT, {
        'Total Athletes': res.data.data.length,
        teamTwrn,
      });
    }
    const eligibleAthletes = res.data.data.map((athlete) => new EligibleAthlete(athlete, teamTwrn));
    return eligibleAthletes.sort((a, b) => a.lastName.localeCompare(b.lastName) || a.firstName.localeCompare(b.firstName));
  };

  // restore deleted athlete to budget
  const restoreDeletedAthlete = async (teamTwrn: string, budgetTwrn: string, payload: RestoreDeletedAthleteRequestModel) => {
    const res = await axios.post(`teams/${teamTwrn}/budgets/${budgetTwrn}/athletes/restore`, payload);
    return res.data;
  };

  const updateAthletes = async (payload: {
    twrn: string;
    email: string;
  }[]) => {
    await axios.patch(`athletes`, { athletes: payload });
  };


  const getContractTemplates = async (
    teamTwrn: string,
    options?: {
      cursor?: string;
      limit?: number;
    },
  ): Promise<ContractTemplateResponse> => {
    const url = `/teams/${teamTwrn}/contract-templates`;

    const res = await axios.get<{
      data: ApiContractTemplate[];
      metadata: ApiPaginationMetadata;
    }>(url, {
      params: {
        cursor: options?.cursor,
        limit: options?.limit,
      },
    });

    return {
      templates: res.data.data.map((template) => new ContractTemplate(template)),
      metadata: res.data.metadata,
    };
  };

  const unlinkBudgetFromAgreement = async (teamTwrn: string, agreementTwrn: string) => {
    const res = await axios.post(`/teams/${teamTwrn}/agreements/${agreementTwrn}/update-budget`, { budget_twrn: null });
    return res.data;
  };

  // @TODO: Add filter types to the request
  const getPaymentRequests = async (teamTwrn: string, agreementTwrn: string, filters: ApiPaymentRequestListingRequestBody) => {
    const res = await axios.post(`/teams/${teamTwrn}/agreements/${agreementTwrn}/payments/list`, filters);
    const paymentRequests: PaymentRequest[] = res.data.data.map((paymentRequest: ApiPaymentRequest) => new PaymentRequest(paymentRequest));
    return {
      paymentRequests,
      metadata: new PaginationMetadata(res.data.metadata),
    };
  };

  const getTeamPaymentRequests = async (teamTwrn: string, filters: ApiPaymentRequestListingRequestBody) => {
    const res = await axios.post(`/teams/${teamTwrn}/agreements/payments/list`, filters);
    const paymentRequests: PaymentRequestWithAgreement[] = res.data.data.map((paymentRequest: ApiPaymentRequestWithAgreement) => new PaymentRequestWithAgreement(paymentRequest));
    return {
      paymentRequests,
      metadata: new PaginationMetadata(res.data.metadata),
    };
  };

  const exportTeamPaymentRequests = async (teamTwrn: string, filters: ApiPaymentRequestListingRequestBody) => {
    await axios.post(`/teams/${teamTwrn}/agreements/payments/export`, filters);
  };

  const editPaymentRequest = async (teamTwrn: string, agreementTwrn: string, paymentRequestTwrn: string, paymentRequest: UpdatePaymentRequestRequestModel) => {
    await axios.put(`/teams/${teamTwrn}/agreements/${agreementTwrn}/payments/${paymentRequestTwrn}`, paymentRequest);
  };

  // create agreement
  const createOrUpdateAgreement = async (
    teamTwrn: string,
    agreement: CreateOrUpdateAgreementRequestModel,
    agreementTwrn?: string,
  ) => {
    const url = agreementTwrn
      ? `teams/${teamTwrn}/agreements/${agreementTwrn}`
      : `teams/${teamTwrn}/agreements`;
    const res = await axios.put<ApiAgreement>(
      url,
      agreement,
    );
    if (res.status === 201) {
      trackEvent(EVENTS.AGREEMENT_CREATED, {
        teamTwrn,
        'Number of Athletes': agreement.athletes?.length ?? 0,
        'Total Contract Value': calculateTotalValue(agreement.athletes ?? [], (athlete) => calculateTotalValue(athlete.payments, (payment) => payment.value)),
        'Template Used': agreement.contract_template ? true : false,
        'Included Reviewer': Object.values(agreement.reviewers ?? {}).some(
          (reviewers) => reviewers.length > 0,
        ),
      });
    }
    return res.data;
  };

  const getAgreementStats = async (teamTwrn: string, agreementTwrn: string) => {
    const res = await axios.get<ApiAgreementStats>(
      `teams/${teamTwrn}/agreements/${agreementTwrn}/stats`,
    );
    return new AgreementStats(res.data);
  };

  const getAgreementAthletes = async (teamTwrn: string, agreementTwrn: string, limit?: number) => {
    const res = await axios.get<{ data: ApiAgreementAthlete[] }>(
      `/teams/${teamTwrn}/agreements/${agreementTwrn}/agreement-athletes`,
      { params: { limit } },
    );
    return res.data.data.map((athlete) => new AgreementAthlete(athlete, teamTwrn));
  };

  const getAthleteContract = async (
    teamTwrn: string,
    agreementTwrn: string,
    athleteTwrn: string,
  ) => {
    const res = await axios.get<ApiAthleteContract>(
      `teams/${teamTwrn}/agreements/${agreementTwrn}/agreement-athletes/${athleteTwrn}`,
    );
    const sortedParticipants = (res.data.participants || []).sort((a, b) => a.signing_order - b.signing_order);
    return new AthleteContract({
      ...res.data,
      participants: sortedParticipants,
    });
  };

  // cancel agreement
  const cancelAgreement = async (teamTwrn: string, agreementTwrn: string) => {
    const res = await axios.delete(`teams/${teamTwrn}/agreements/${agreementTwrn}`);
    return res.data;
  };


  // POST teams/{team}/agreements/{agreement}/agreement-athletes/{agreementAthlete}/cancel
  const cancelAthleteAgreement = async (teamTwrn: string, agreementTwrn: string, agreementAthleteTwrn: string) => {
    const res = await axios.post(`teams/${teamTwrn}/agreements/${agreementTwrn}/agreement-athletes/${agreementAthleteTwrn}/cancel`);
    return res.data;
  };

  const getAwardCategories = async (teamBudgetTwrn?: string, academicYear?: string) => {
    const res = await axios.get<{ data: ApiAwardCategory[] }>(`/scholarships/award-categories`, {
      params: {
        academic_year: academicYear,
        team_budget: teamBudgetTwrn,
      },
    });
    // return list as new AwardCategory[] ordered by display_order
    return res.data.data.map((awardCategory) => new AwardCategory(awardCategory)).sort((a, b) => a.order - b.order);
  };

  // add award category
  const addAwardCategory = async (awardCategory: AwardCategoryPayload) => {
    const res = await axios.post(`scholarships/award-categories`, awardCategory);
    return res.data;
  };

  // edit award category
  const editAwardCategory = async (awardCategory: AwardCategoryPayload) => {
    const res = await axios.patch(`scholarships/award-categories/${awardCategory.twrn}`, awardCategory);
    return res.data;
  };

  const deleteAwardCategory = async (awardCategoryTwrn: string) => {
    const res = await axios.delete(`scholarships/award-categories/${awardCategoryTwrn}`);
    return res.data;
  };

  const orderAwardCategories = async (academicYear: string, awardCategories: string[]) => {
    const res = await axios.put(`scholarships/award-categories/ordering`, {
      academic_year: academicYear,
      award_categories: awardCategories,
    });
    return res.data;
  };

  // get scholarship packages

  const getScholarshipPackages = async (teamBudgetTwrn?: string, academicYear?: string) => {
    const res = await axios.get<{ data: ApiScholarshipPackageListing[] }>(`/scholarships/packages`, {
      params: {
        academic_year: academicYear,
        team_budget: teamBudgetTwrn,
      },
    });
    return res.data.data.map((scholarshipPackage) => new ScholarshipPackageListing(scholarshipPackage));
  };

  // create scholarship package
  const createScholarshipPackage = async (scholarshipPackage: CreateScholarshipPackageRequestModel) => {
    const res = await axios.post(`/scholarships/packages`, scholarshipPackage);
    return res.data;
  };

  // edit scholarship package
  const editScholarshipPackage = async (twrn: string, scholarshipPackage: CreateScholarshipPackageRequestModel) => {
    const res = await axios.patch(`/scholarships/packages/${twrn}`, scholarshipPackage);
    return res.data;
  };

  // delete scholarship package
  const deleteScholarshipPackage = async (scholarshipPackageTwrn: string) => {
    const res = await axios.delete(`/scholarships/packages/${scholarshipPackageTwrn}`);
    return res.data;
  };

  const getAcademicYears = async () => {
    const res = await axios.get<{ data: ApiAcademicYear[] }>('/academic-year-dates');
    const academicYears = res.data.data.map((academicYear) => new AcademicYear(academicYear));
    // Sort by startDate in ascending order
    return academicYears.sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
  };

  // rollover scholarships
  const rolloverScholarships = async (sourceAcademicYear: string, targetAcademicYear: string) => {
    const res = await axios.post(`/scholarships/rollover`, {
      source_academic_year: sourceAcademicYear,
      target_academic_year: targetAcademicYear,
    });
    return res.data;
  };

  const sendPaymentRequests = async (teamTwrn: string, payments: string[], paymentRequestType?: PaymentRequestType) => {
    const res = await axios.post(`/teams/${teamTwrn}/agreements/payments/send`, {
      payments,
      payment_type: paymentRequestType,
    });

    if (res.status === 204) {
      trackEvent(EVENTS.SENT_PAYMENTS_TO_WALLET, {
        teamTwrn,
        'Number of Payments': payments.length,
      });
    }

    return res.data;
  };

  const cancelPaymentRequest = async (teamTwrn: string, agreementTwrn: string, paymentRequestTwrn: string, reason: string) => {
    const res = await axios.delete(`/teams/${teamTwrn}/agreements/${agreementTwrn}/payments/${paymentRequestTwrn}`, {
      data: {
        reason,
      },
    });
    return res.data;
  };

  const updateAthleteScholarships = async (teamTwrn: string, budgetTwrn: string, scholarships: UpdateAthleteScholarshipsRequestModel) => {
    const res = await axios.put(`/teams/${teamTwrn}/budgets/${budgetTwrn}/scholarships`, scholarships);
    return res.data;
  };

  const getLinkableProfiles = async (teamTwrn: string, search: string, cursor?: string | null) => {
    if (!search) {
      throw new Error('Search is required');
    }
    const res = await axios.get<ApiLinkableProfilesResponse>(`/teams/${teamTwrn}/tw-profiles?keyword=${search}&cursor=${cursor ?? ''}`);
    return new LinkableProfilesResponse(res.data);
  };

  const linkTWAthlete = async (teamTwrn: string, athleteTwrn: string, axleTwrn?: string) => {
    const res = await axios.post(`/teams/${teamTwrn}/athletes/${athleteTwrn}/tw-link`, {
      axle_twrn: axleTwrn,
    });

    return res.data;
  };

  const mergeTWAthlete = async (teamTwrn: string, athleteTwrn: string, gmGhostTwrn: string) => {
    const res = await axios.post(`/teams/${teamTwrn}/athletes/${athleteTwrn}/merge`, {
      twrn: gmGhostTwrn,
    });

    return res.data;
  };

  // Org Budgets
  const getOrgBudgetsOverview = async (academicYear: string) => {
    const res = await axios.get<ApiOrgBudgetOverview>(`/budgets/overview?academic_year=${academicYear}`);
    return new OrgBudgetOverviewSummary(res.data);
  };

  const getOrgBudgetsList = async (requestBody?: {
    cursor?: string | null;
    per_page?: number;
  }) => {
    const res = await axios.post<ApiOrgBudgetList>(`/budgets/list`, requestBody);
    return {
      data: res.data.data.map((budget) => new OrgBudget(budget)),
      metadata: new PaginationMetadata(res.data.metadata),
    };
  };

  const getTeamAthletesList = async (teamTwrn: string, filters: ApiTeamAthleteListingRequestBody) => {
    const res = await axios.post<{
      data: ApiTeamAthlete[];
      metadata: ApiPaginationMetadata;
    }>(`/teams/${teamTwrn}/athletes/list`, filters);
    return {
      data: res.data.data.map((athlete) => new TeamAthlete(athlete, teamTwrn)),
      metadata: new PaginationMetadata(res.data.metadata),
    };
  };

  const getTeamAthlete = async (teamTwrn: string, athleteTwrn: string) => {
    const res = await axios.get<ApiTeamAthleteProfile>(`/teams/${teamTwrn}/athletes/${athleteTwrn}`);
    return new TeamAthleteProfile(res.data);
  };

  const generateAgreementPreview = async (
    teamTwrn: string,
    requestBody: GenerateAgreementPreviewRequestModel,
  ) => {
    const res = await axios.post<ApiAgreementPreview>(`/teams/${teamTwrn}/agreements/preview`, requestBody);
    return new AgreementPreview(res.data);
  };

  const removeAthlete = async (teamTwrn: string, athleteTwrn: string, requestBody: RemoveAthleteRequestModel) => {
    const res = await axios.post(`/teams/${teamTwrn}/athletes/${athleteTwrn}/remove-from-roster`, requestBody);
    return res.data;
  };

  const deleteAthleteAgreement = async (teamTwrn: string, agreementTwrn: string, agreementAthleteTwrn: string) => {
    const res = await axios.delete(`/teams/${teamTwrn}/agreements/${agreementTwrn}/agreement-athletes/${agreementAthleteTwrn}`);
    return res.data;
  };

  const getUserPermissionsList = async () => {
    const res = await axios.get<ApiUserPermissionsListingResponse>(`/users`);
    return res.data.data?.map((user) => new UserPermissionListing(user)).sort((a, b) => a.fullName.localeCompare(b.fullName));
  };

  const getUserPermissions = async (userTwrn: string) => {
    const res = await axios.get<ApiUserPermissionsResponse>(`/users/${userTwrn}/user-permissions`);
    return res.data.data.map((permission) => new Permission(permission));
  };

  const getTeamPositions = async (teamTwrn: string) => {
    const res = await axios.get<{ data: ApiTeamPosition[] }>(`/teams/${teamTwrn}/positions`);
    const positions = (res.data.data ?? []).map((position) => new TeamPosition(position));
    TeamPosition.register(teamTwrn, positions);
    return positions.sort((a, b) => a.name.localeCompare(b.name));
  };

  const getAllTeams = async () => {
    const res = await axios.get<ApiTeamsListingResponse>(`/teams`);
    return new TeamsListingResponse(res.data);
  };

  const saveUserPermissions = async (userTwrn: string, payload: ApiPermission[]) => {
    const res = await axios.put(`/users/${userTwrn}/user-permissions`, {
      data: payload,
    });
    return res.data;
  };


  return {
    loginAxle,
    swapAxleTwrnForGmTwrn,
    getAxleProfile,
    getMe,
    getOutgoingAxleToken,
    getTeamBudgets,
    editBudget,
    changeBudgetStatus,
    deleteBudget,
    createPlayer,
    deleteAthlete,
    savePositionTargets,
    saveLineItems,
    logout,
    getTags,
    createTag,
    updateTag,
    applyTag,
    removeTag,
    updateAthleteOrder,
    getAgreements,
    getAgreementsByContract,
    getEligibleAthletes,
    restoreDeletedAthlete,
    updateAthletes,
    getContractTemplates,
    unlinkBudgetFromAgreement,
    getPaymentRequests,
    getTeamPaymentRequests,
    exportTeamPaymentRequests,
    getDraftAgreement,
    createOrUpdateAgreement,
    getContractTemplate,
    getAgreementStats,
    getAgreementAthletes,
    getAthleteContract,
    cancelAgreement,
    cancelAthleteAgreement,
    getScholarshipPackages,
    getAwardCategories,
    createScholarshipPackage,
    editScholarshipPackage,
    getAcademicYears,
    addAwardCategory,
    editAwardCategory,
    deleteAwardCategory,
    deleteScholarshipPackage,
    orderAwardCategories,
    editPaymentRequest,
    sendPaymentRequests,
    cancelPaymentRequest,
    updateAthleteScholarships,
    rolloverScholarships,
    getLinkableProfiles,
    getOrgBudgetsOverview,
    getOrgBudgetsList,
    linkTWAthlete,
    mergeTWAthlete,
    getTeamAthletesList,
    getTeamAthlete,
    generateAgreementPreview,
    removeAthlete,
    deleteAthleteAgreement,
    getUserPermissionsList,
    getUserPermissions,
    getTeamPositions,
    getAllTeams,
    saveUserPermissions,
  };
}
